import { useEffect, useState, useMemo } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { editorPagesAtom } from '@/atoms/_editorAtoms'
import { caseAtom } from '@/atoms/caseAtoms'
import {
  imageQualityAtom,
  type ImageQualityInfo,
} from '@/atoms/imageQualityAtoms'
import {
  IMAGE_QUALITY_BANNER_PPI_THRESHOLD,
  IMAGE_QUALITY_PAPER_PPI_THRESHOLD,
} from '@messenger/consts'
import { calculateImageQuality } from '@/utils/editor/calculateImageQuality'
import { calculateProductPPI } from '@/utils/editor/calculateProductPPI'
import { determineImageUrl } from '@/utils/editor/determineImageUrl'
import { isImagePlaceholder } from '@/utils/editor/imagePlaceholder'
import type { Content, ImageItem } from '@messenger/types'

type UseAllPagesImageQualityCheckArgs = {
  enabled?: boolean
  debounceMs?: number
}

/**
 * Hook to check image quality across ALL pages in the editor, not just the current page.
 *
 * This hook processes all images in all pages stored in editorPagesAtom and populates
 * the imageQualityAtom with quality assessment results. This enables:
 * - Page load quality warnings for images on non-visible pages
 * - Save-time warnings for all images across the entire product
 * - Complete quality overview without requiring page navigation
 *
 * The hook runs independently of the UI rendering and uses the same calculation
 * logic as individual PageImage components but applies it to all pages at once.
 */
export const useAllPagesImageQualityCheck = ({
  enabled = true,
  debounceMs = 500,
}: UseAllPagesImageQualityCheckArgs = {}) => {
  const editorPages = useRecoilValue(editorPagesAtom)
  const _case = useRecoilValue(caseAtom)
  const setImageQuality = useSetRecoilState(imageQualityAtom)
  const [isProcessing, setIsProcessing] = useState(false)
  const [processedCount, setProcessedCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)

  const isBannerProduct = useMemo(() => {
    const firstPage = editorPages?.[0]
    const productType = firstPage?.pageProperties?.product
    return productType?.toLowerCase()?.includes('banner') || false
  }, [editorPages])

  const minimumPixelsPerInch = useMemo(() => {
    return isBannerProduct
      ? IMAGE_QUALITY_BANNER_PPI_THRESHOLD
      : IMAGE_QUALITY_PAPER_PPI_THRESHOLD
  }, [isBannerProduct])

  const allImageItems = useMemo(() => {
    const noPages = !editorPages || !Array.isArray(editorPages)
    if (noPages) return []
    // TODO: fix when we update caseAtom to typescript
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filledImages = (_case as any)?.filledImages

    const imageItems: Array<{
      imageId: string
      imageUrl: string | null
      pageIndex: number
      item: ImageItem
      page: Content
    }> = []

    editorPages.forEach((page, pageIndex) => {
      const items = page?.product?.items || []
      items.forEach((item) => {
        const isImage = item.type === 'image'
        const isDesignAsset =
          isImage && item.properties?.subtype === 'designAsset'
        const isImageAndNotDesignAsset = isImage && !isDesignAsset
        if (isImageAndNotDesignAsset) {
          // Type narrow to ImageItem since we confirmed item.type === 'image'
          const imageItem = item as ImageItem
          const imageId = `${imageItem.type}-${imageItem.id}`

          const imageUrl = determineImageUrl({
            url: imageItem.image?.url,
            editedUrl: imageItem.image?.editedUrl,
            subtype: imageItem.properties?.subtype,
            caseImage: imageItem.properties?.caseImage,
            filledImages,
          })

          const originalUrl = imageItem.image?.url
          const isOriginalUrlPlaceholder =
            originalUrl && isImagePlaceholder(originalUrl)
          const imageUrlWasResolved = imageUrl && imageUrl !== originalUrl
          const shouldIncludeImage =
            imageUrl && (!isOriginalUrlPlaceholder || imageUrlWasResolved)

          if (shouldIncludeImage) {
            imageItems.push({
              imageId,
              imageUrl,
              pageIndex,
              item: imageItem,
              page,
            })
          }
        }
      })
    })

    return imageItems
  }, [editorPages, _case])

  const getProductPPI = (page: Content): number | null => {
    const productSize = page?.product?.productSize
    const pageSize = page?.pageProperties?.pageSize
    return calculateProductPPI(productSize, pageSize)
  }

  useEffect(() => {
    const doNotProcessOrUpdateTotalCount =
      !enabled || allImageItems.length === 0
    if (doNotProcessOrUpdateTotalCount) {
      setProcessedCount(0)
      setTotalCount(0)
      return
    }

    let isCancelled = false
    setIsProcessing(true)
    setProcessedCount(0)
    setTotalCount(allImageItems.length)

    const processAllImages = async () => {
      const qualityResults: ImageQualityInfo[] = []

      for (const [
        index,
        { imageId, imageUrl, item, page },
      ] of allImageItems.entries()) {
        if (isCancelled) break

        try {
          const productPPI = getProductPPI(page)
          const canProcessImage =
            productPPI && item.width && item.height && imageUrl
          if (canProcessImage) {
            const result = await calculateImageQuality({
              imageUrl,
              displayedBoxWidthPixels: item.width,
              displayedBoxHeightPixels: item.height,
              cropLeftPixels: item.cropLeft || 0,
              cropRightPixels: item.cropRight || 0,
              cropTopPixels: item.cropTop || 0,
              cropBottomPixels: item.cropBottom || 0,
              productPixelsPerInch: productPPI,
              minimumPixelsPerInch,
            })

            const effectivePixelsPerInchIsNotNull =
              result.effectivePixelsPerInch !== null
            if (effectivePixelsPerInchIsNotNull) {
              qualityResults.push({
                imageId,
                hasQualityIssue: result.isBelowPrintQualityThreshold,
                effectivePixelsPerInch: result.effectivePixelsPerInch,
                threshold: minimumPixelsPerInch,
              })
            }
          }
        } catch (error) {
          console.warn(`Failed to process image quality for ${imageId}:`, error)
        }

        setProcessedCount(index + 1)
      }

      if (!isCancelled) {
        setImageQuality((prevQualities) => {
          const currentImageIds = new Set(
            allImageItems.map((item) => item.imageId),
          )
          const processedImageIds = new Set(
            qualityResults.map((r) => r.imageId),
          )
          const filteredPrevious = prevQualities.filter(
            (q) =>
              currentImageIds.has(q.imageId) &&
              !processedImageIds.has(q.imageId),
          )
          return [...filteredPrevious, ...qualityResults]
        })
        setIsProcessing(false)
      }
    }

    // Debounce the processing to avoid excessive calls
    const timeoutId = setTimeout(processAllImages, debounceMs)

    return () => {
      isCancelled = true
      clearTimeout(timeoutId)
      setIsProcessing(false)
    }
  }, [
    allImageItems,
    enabled,
    minimumPixelsPerInch,
    debounceMs,
    setImageQuality,
  ])

  return {
    isProcessing,
    processedCount,
    totalCount,
    progressPercentage:
      totalCount > 0 ? Math.round((processedCount / totalCount) * 100) : 0,
  }
}
